import { Helpers } from '@helpers';
import { map, switchMap } from 'rxjs/operators';
import { AwareHttpService, IAwareCollection } from '@appbolaget/aware-http';
import { AwareHttpRequestModule } from '@appbolaget/aware-http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { AwareHttpRequest } from '@appbolaget/aware-http';
import { AwareModel, CrudMixin } from '@appbolaget/aware-model';
import { Autocompletable } from '@helpers/interfaces';

type AwareModelWithCrud = AwareModel & CrudMixin;

export abstract class ModelService<T extends AwareModelWithCrud> implements Autocompletable {
    model: T;

    constructor(protected api: AwareHttpService) {}

    delete(obj: T) {
        return obj.delete(this.api);
    }

    autocomplete({ search }: { search: string; filter: any }): Observable<T[]> {
        return this.search(search)
            .execute()
            .pipe(map((response) => response.data));
    }

    getAll<T2 = IAwareCollection<T>>(params?: any, headers?: any): AwareHttpRequest<T2> {
        return this.api
            .get(this.model.model.route)
            .parameters(params)
            .module(this.model.model.module ?? AwareHttpRequestModule.Cms)
            .headers(headers)
            .toCollection(this.model.model);
    }

    get<T2 = T>(id: string | number): AwareHttpRequest<T2> {
        return this.api
            .show(this.model.model.route, id)
            .module(this.model.model.module ?? AwareHttpRequestModule.Cms)
            .toModel(this.model.model);
    }

    search<T2 = IAwareCollection<T>>(query: string): AwareHttpRequest<T2> {
        return this.api
            .get(this.model.model.searchRoute)
            .parameters({ query })
            .module(this.model.model.module ?? AwareHttpRequestModule.Cms)
            .toCollection(this.model.model);
    }

    async getAsync(id: string | number, params?: any): Promise<T> {
        const response: T = await firstValueFrom(
            this.api.show(this.model.model.route, id).parameters(params).toModel(this.model.model).execute(),
        );

        return response;
    }

    /**
     *
     * @param uuid Base model id
     * @param foreignType Model type to sync with
     * @param originalArray Original source
     * @param newArray New source
     * @param pivotOptions Extra data for the pivot object
     * @param anchorType Anchor model type
     * @param setPosition Set to true to set position of array index
     */
    getAttachAndDetachRequests(
        uuid: string,
        foreignType: string,
        originalArray: AwareModel[],
        newArray: AwareModel[],
        pivotOptions?: { status?: string; meta?: any },
        anchorType?: string,
        setPosition = true,
    ): [AwareHttpRequest, AwareHttpRequest] {
        const attach = newArray
            ? newArray.map((model, index) => {
                  const obj: any = { id: model.uuid, fields: {} };

                  if (setPosition) {
                      obj.fields.position = index;
                  }

                  if (pivotOptions?.status) {
                      obj.fields.status = pivotOptions.status;
                  }

                  return obj;
              })
            : [];

        const removed = originalArray ? originalArray.filter((model) => !newArray.find((obj) => obj.uuid === model.uuid)) : [];

        const detach = removed.map((model) => {
            return {
                id: model.uuid,
            };
        });

        const attachRequest = this.api
            .put(`${anchorType ? anchorType : this.model.model.route}/${uuid}/attach/${foreignType}`, attach)
            .stream('up');
        const detachRequest = this.api
            .put(`${anchorType ? anchorType : this.model.model.route}/${uuid}/detach/${foreignType}`, detach)
            .stream('up');

        return [attach.length ? attachRequest : null, detach.length ? detachRequest : null];
    }

    save<T>(values: any): Observable<T> {
        if (values.meta && typeof values.meta !== 'string') {
            values.meta = Helpers.prepareMetaForRequest(values.meta);
        }

        const thing = new this.model.model(values);
        return thing
            .save(this.api, { fields: ['title', 'meta', 'type'], data: { ...values } })
            .toModel(this.model.model)
            .execute();
    }

    /**
     *
     * @param uuid Base model id
     * @param foreignType Model type to sync with
     * @param originalArray Original source
     * @param newArray New source
     * @param anchorType Anchor model type
     */
    sync(uuid: string, foreignType: string, originalArray: AwareModel[], newArray: AwareModel[], anchorType?: string): Observable<any> {
        const [attachRequest, detachRequest] = this.getAttachAndDetachRequests(
            uuid,
            foreignType,
            originalArray,
            newArray,
            null,
            anchorType,
        );

        if (!attachRequest) {
            return detachRequest.execute();
        }

        return attachRequest.execute().pipe(
            switchMap((result) => {
                if (!detachRequest) {
                    return of(result);
                }

                return detachRequest.execute();
            }),
        );
    }
}
